import React, { useMemo } from 'react';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import CancelIcon from '@material-ui/icons/Cancel';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import FPButtonMenu from 'UI/components/molecules/FPButtonMenu';

import { DateRangeFilter } from '../DateRangeFilter';
import { SortSelect } from '../SortSelect';

import { useStyles } from './ResumeSearchToolbar.styled';
import { buildToolbarActions } from './ResumeSearchToolbar.utils';
import { useResponsiveToolbar } from './useResponsiveToolbar';

const COPIES = strings.shared.savedSearches;
const EMPTY_BUTTON_DEFINITIONS = [];

export const ResumeSearchToolbar = ({
  isLoading,
  dateRangeValue,
  handleDateRangeChange,
  sortId,
  handleSortChange,
  savedSearchButtonDefinitions = EMPTY_BUTTON_DEFINITIONS,
  activeSearch,
  savedSearchAppliedProps
}) => {
  const classes = useStyles();

  const actions = useMemo(
    () =>
      buildToolbarActions({
        savedSearchButtonDefinitions,
        activeSearch,
        savedSearchAppliedProps,
        copies: COPIES
      }),
    [savedSearchButtonDefinitions, activeSearch, savedSearchAppliedProps]
  );
  const actionByKey = useMemo(
    () => actions.reduce((acc, action) => ({ ...acc, [action.key]: action }), {}),
    [actions]
  );

  const {
    chipRef,
    filtersRef,
    inlineActionKeys,
    isOverflowOpen,
    overflowActionKeys,
    registerMeasurementRef,
    setIsOverflowOpen,
    setRootRef,
    triggerRef
  } = useResponsiveToolbar({ actions, activeSearch, isLoading, dateRangeValue, sortId });

  const overflowItems = overflowActionKeys.map(key => ({
    key,
    title: actionByKey[key].text,
    visible: true,
    action: actionByKey[key].onClick
  }));
  const hasToolbarContext = actions.length > 0 || !!activeSearch;

  return (
    <Box className={classes.root} ref={setRootRef}>
      <Box className={classes.filtersGroup} ref={filtersRef}>
        <DateRangeFilter
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          disabled={isLoading}
        />
        <SortSelect value={sortId} onChange={handleSortChange} disabled={isLoading} />
      </Box>
      <When condition={hasToolbarContext}>
        <Box className={classes.actionsGroup}>
          <When condition={!!activeSearch}>
            <Chip
              ref={chipRef}
              data-testid="active-search-chip"
              disabled={isLoading}
              classes={{ root: classes.chipRoot, label: classes.chipLabel }}
              label={activeSearch?.name}
              title={activeSearch?.name}
              deleteIcon={<CancelIcon aria-label={COPIES.removeAppliedSearch} />}
              onDelete={savedSearchAppliedProps?.onRemoveSearch}
            />
          </When>
          {inlineActionKeys.map(key => {
            const { key: _key, ...actionProps } = actionByKey[key];
            return (
              <FPActionButton
                key={key}
                data-toolbar-action-key={key}
                disabled={isLoading}
                size="small"
                {...actionProps}
              />
            );
          })}
          <When condition={!!overflowItems.length}>
            <FPButtonMenu
              isIconButton
              disabled={isLoading}
              menuItems={overflowItems}
              title={COPIES.searchActions}
              open={isOverflowOpen}
              onOpenChange={setIsOverflowOpen}
              triggerRef={triggerRef}
            />
          </When>
        </Box>
      </When>
      <Box aria-hidden="true" className={classes.measurementRow}>
        {actions.map(({ key, ...actionProps }) => (
          <Box component="span" key={key} data-measure-key={key} ref={registerMeasurementRef(key)}>
            <FPActionButton size="small" {...actionProps} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
